import React from "react";
import SectionError from "./section_error";
import {
    updateAccount,
    deleteAccount,
    saveCredentials,
    deleteCredentials,
} from "../../services/stockApi";
import { showToast } from "../../utils/show_toast";
import { Confirm } from "../../utils/confirm";

// API 키 연동을 지원하는 증권사 — mirae는 개인용 Open API가 없어 수기 입력 전용이다
// (go-server/internal/model/stock_merge.go BrokerMirae 주석과 짝을 이룬다).
const API_CAPABLE_BROKERS = ["kiwoom", "kiwoom_isa"];

/**
 * AccountKeyCard — 계좌 1건 카드 (DAS-171 P1 E-2, broker_key_card.js 후신).
 *
 * broker_key_card.js와 달리 자체 fetch를 하지 않는다 — 부모(my_page.js)가 계좌 목록을
 * 일괄 조회해 `account` prop으로 내려주고(registered/app_key_masked/modified 포함),
 * 이 카드는 변경 액션(별칭 수정·합계 포함 토글·키 등록/변경/삭제·계좌 삭제)만 수행한
 * 뒤 `onUpdated`/`onDeleted`로 부모 상태를 갱신시킨다.
 */
class AccountKeyCard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            editingLabel: false,
            labelDraft: props.account.label || "",
            savingLabel: false,
            togglingTotal: false,
            editingKey: false,
            appKey: "",
            secretKey: "",
            savingKey: false,
            deletingKey: false,
            deletingAccount: false,
        };
    }

    isApiCapable() {
        return API_CAPABLE_BROKERS.includes(this.props.account.broker);
    }

    onStartEditLabel = () => {
        this.setState({ editingLabel: true, labelDraft: this.props.account.label || "" });
    };

    onSaveLabel = () => {
        const { account, onUpdated } = this.props;
        const label = this.state.labelDraft.trim();
        this.setState({ savingLabel: true });
        updateAccount(account.account_id, { label })
            .then((res) => {
                showToast({ type: "success", message: "별칭을 저장했습니다." });
                this.setState({ editingLabel: false, savingLabel: false });
                onUpdated(res.data);
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || "별칭 저장에 실패했습니다.";
                showToast({ type: "error", message: msg });
                this.setState({ savingLabel: false });
            });
    };

    onToggleIncludeInTotal = () => {
        const { account, onUpdated } = this.props;
        const nextValue = !account.include_in_total;
        this.setState({ togglingTotal: true });
        updateAccount(account.account_id, { include_in_total: nextValue })
            .then((res) => {
                showToast({
                    type: "success",
                    message: nextValue ? "합계에 포함했습니다." : "합계에서 제외했습니다.",
                });
                this.setState({ togglingTotal: false });
                onUpdated(res.data);
            })
            .catch(() => {
                showToast({ type: "error", message: "설정 변경에 실패했습니다." });
                this.setState({ togglingTotal: false });
            });
    };

    onSaveKey = () => {
        const { account, onUpdated } = this.props;
        const appKey = this.state.appKey.trim();
        const secretKey = this.state.secretKey.trim();
        if (!appKey || !secretKey) {
            showToast({ type: "error", message: "App Key와 Secret Key를 모두 입력하세요." });
            return;
        }
        this.setState({ savingKey: true });
        saveCredentials({ app_key: appKey, secret_key: secretKey }, { accountId: account.account_id })
            .then(() => {
                showToast({ type: "success", message: "API 키를 등록했습니다." });
                this.setState({ editingKey: false, appKey: "", secretKey: "", savingKey: false });
                // 서버가 마스킹 키를 계산해 내려주므로 계좌 목록을 다시 받아야 한다 — 부모에 위임.
                onUpdated({ ...account, registered: true });
                this.props.onRefetch?.();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || "키 등록에 실패했습니다.";
                showToast({ type: "error", message: msg });
                this.setState({ savingKey: false });
            });
    };

    onDeleteKey = async () => {
        const { account, onUpdated } = this.props;
        const ok = await Confirm.show({
            title: "API 키 삭제",
            message: "등록된 App Key/Secret Key를 삭제합니다. 계좌 자체는 유지됩니다. 계속할까요?",
            confirmText: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        this.setState({ deletingKey: true });
        deleteCredentials({ accountId: account.account_id })
            .then(() => {
                showToast({ type: "success", message: "API 키를 삭제했습니다." });
                this.setState({ deletingKey: false });
                onUpdated({ ...account, registered: false, app_key_masked: "" });
            })
            .catch(() => {
                showToast({ type: "error", message: "키 삭제에 실패했습니다." });
                this.setState({ deletingKey: false });
            });
    };

    onDeleteAccount = async () => {
        const { account, onDeleted } = this.props;
        const ok = await Confirm.show({
            title: "계좌 삭제",
            message: "이 계좌를 목록에서 삭제합니다. 기존 매수 이력은 보존되며 계좌 귀속만 해제됩니다. 계속할까요?",
            confirmText: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        this.setState({ deletingAccount: true });
        deleteAccount(account.account_id)
            .then(() => {
                showToast({ type: "success", message: "계좌를 삭제했습니다." });
                onDeleted(account.account_id);
            })
            .catch(() => {
                showToast({ type: "error", message: "계좌 삭제에 실패했습니다." });
                this.setState({ deletingAccount: false });
            });
    };

    renderLabelRow() {
        const { account } = this.props;
        const { editingLabel, labelDraft, savingLabel } = this.state;
        const displayName = account.label || account.broker_label;

        if (editingLabel) {
            return (
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        type="text"
                        value={labelDraft}
                        onChange={(e) => this.setState({ labelDraft: e.target.value })}
                        maxLength={40}
                        placeholder={account.broker_label}
                        className="min-w-0 flex-1 rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                    />
                    <button
                        type="button"
                        onClick={() => this.setState({ editingLabel: false })}
                        className="min-h-[44px] rounded-lg border border-surface-border px-3 text-xs text-content-secondary hover:text-content"
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        onClick={this.onSaveLabel}
                        disabled={savingLabel}
                        className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 text-xs font-medium text-brand-light hover:bg-brand/30 disabled:opacity-50"
                    >
                        {savingLabel ? "저장 중…" : "저장"}
                    </button>
                </div>
            );
        }

        return (
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-content">{displayName}</span>
                <span className="rounded-full bg-brand-subtle px-2 py-0.5 text-xs text-content-secondary">
                    {account.broker_label}
                </span>
                <button
                    type="button"
                    onClick={this.onStartEditLabel}
                    className="text-xs text-content-muted underline decoration-dotted hover:text-content-secondary"
                >
                    별칭 수정
                </button>
            </div>
        );
    }

    renderKeyForm() {
        const { appKey, secretKey, savingKey } = this.state;
        const idPrefix = `account-${this.props.account.account_id}-key`;
        const inputCls =
            "w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand";
        return (
            <div className="mt-3 flex flex-col gap-2">
                <label className="text-xs text-content-secondary" htmlFor={`${idPrefix}-app`}>
                    App Key
                </label>
                <input
                    id={`${idPrefix}-app`}
                    type="text"
                    autoComplete="off"
                    value={appKey}
                    onChange={(e) => this.setState({ appKey: e.target.value })}
                    className={inputCls}
                    placeholder="키움 개발자센터에서 발급한 App Key"
                />
                <label className="text-xs text-content-secondary" htmlFor={`${idPrefix}-secret`}>
                    Secret Key
                </label>
                <input
                    id={`${idPrefix}-secret`}
                    type="password"
                    autoComplete="new-password"
                    value={secretKey}
                    onChange={(e) => this.setState({ secretKey: e.target.value })}
                    className={inputCls}
                    placeholder="Secret Key"
                />
                <div className="mt-1 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => this.setState({ editingKey: false, appKey: "", secretKey: "" })}
                        className="min-h-[44px] rounded-lg border border-surface-border px-3 py-1.5 text-xs text-content-secondary hover:text-content"
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        onClick={this.onSaveKey}
                        disabled={savingKey}
                        className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 py-1.5 text-xs font-medium text-brand-light hover:bg-brand/30 disabled:opacity-50"
                    >
                        {savingKey ? "저장 중…" : "저장"}
                    </button>
                </div>
            </div>
        );
    }

    renderKeySection() {
        const { account } = this.props;
        const { editingKey, deletingKey } = this.state;

        if (!this.isApiCapable()) {
            return (
                <p className="mt-3 text-xs text-content-muted">
                    수기 입력 전용 계좌입니다. API 연동을 지원하지 않으며, 매수 기록으로만 평가액을 집계합니다.
                </p>
            );
        }

        if (editingKey) return this.renderKeyForm();

        if (!account.registered) {
            return (
                <div className="mt-3 flex flex-col items-start gap-2">
                    <p className="text-sm text-content-secondary">
                        등록된 키가 없습니다. 잔고 자동 연동에 사용됩니다.
                    </p>
                    <button
                        type="button"
                        onClick={() => this.setState({ editingKey: true })}
                        className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 py-1.5 text-xs font-medium text-brand-light hover:bg-brand/30"
                    >
                        키 등록
                    </button>
                </div>
            );
        }

        return (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <span className="font-num text-sm text-content">{account.app_key_masked}</span>
                    <span className="text-xs text-content-muted">수정: {account.modified}</span>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => this.setState({ editingKey: true })}
                        className="min-h-[44px] rounded-lg border border-surface-border px-3 py-1.5 text-xs text-content-secondary hover:text-content"
                    >
                        키 변경
                    </button>
                    <button
                        type="button"
                        onClick={this.onDeleteKey}
                        disabled={deletingKey}
                        className="min-h-[44px] rounded-lg border border-surface-border px-3 py-1.5 text-xs text-loss hover:bg-loss-subtle disabled:opacity-50"
                    >
                        키 삭제
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { account } = this.props;
        const { togglingTotal, deletingAccount } = this.state;

        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    {this.renderLabelRow()}
                    <button
                        type="button"
                        onClick={this.onDeleteAccount}
                        disabled={deletingAccount}
                        className="min-h-[44px] shrink-0 rounded-lg border border-surface-border px-3 text-xs text-content-muted hover:text-loss disabled:opacity-50"
                    >
                        계좌 삭제
                    </button>
                </div>

                <label className="mt-3 flex items-center gap-2 text-xs text-content-secondary">
                    <input
                        type="checkbox"
                        checked={!!account.include_in_total}
                        disabled={togglingTotal}
                        onChange={this.onToggleIncludeInTotal}
                        className="h-4 w-4 rounded border-surface-border accent-brand"
                    />
                    총 합계에 포함
                </label>
                <p className="mt-1 text-xs text-content-muted">과거 합계는 소급 재계산되지 않습니다.</p>

                {this.renderKeySection()}

                {this.isApiCapable() && (
                    <p className="mt-3 border-t border-surface-border pt-2 text-xs text-content-muted">
                        키움 개발자센터 앱 등록 시 허용 IP에 서버 공인 IP <span className="font-num">3.34.107.252</span>를
                        추가해야 API 호출이 가능합니다. 키는 서버에만 저장되며 화면에는 마스킹되어 표시됩니다.
                    </p>
                )}
            </div>
        );
    }
}

export default AccountKeyCard;

// AccountKeyCardListError — 계좌 목록 자체를 못 불러왔을 때 부모가 재사용하는 에러 카드.
export function AccountKeyCardListError({ onRetry }) {
    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
            <SectionError message="계좌 목록을 불러오지 못했습니다." onRetry={onRetry} />
        </div>
    );
}
